import { ApplyModes } from 'lecom-api';

/**
 * Classe BaseAPI
 * @class BaseAPI
 */
class BaseAPI {
	_id;
	_storeId;
	_applyMode;
	_storeClass;

	static applyMode = ApplyModes.LAZY;

	/**
	 *
	 * @constructor
	 * @param {*} param0
	 */
	constructor({ id, storeId, applyMode, storeClass }) {
		this._id = id;
		this._storeClass = storeClass;
		this._storeId = storeId || this._getStoreItemId();
		this._applyMode = applyMode;
	}

	get id() {
		return this._id;
	}

	get storeId() {
		return this._storeId;
	}

	get applyMode() {
		return this._applyMode;
	}

	set applyMode(value) {
		this._applyMode = value;
	}

	get storeClass() {
		return this._storeClass;
	}

	get(...args) {		
		return this.getProperties(this.storeClass.store[this.storeId], args);
	}

	/**
	 * @private
	 * @param {*} model
	 * @param {*} properties
	 */
	getProperties(model, properties) {
		if (properties && properties.length) {
			if (properties.length === 1) {				
				if (model) {
					return model[properties[0]];
				}
			}
			else {
				let props = { }
				for (let cont = 0, len = properties.length; cont < len; cont++) {
					if (model && model.hasOwnProperty(properties[cont])) {
						props[properties[cont]] = model[properties[cont]];
					}
				}
				return props;
			}
		}
		return model;
	}

	/**
	 *
	 * @param {*} properties
	 */
	set(properties) {
		const isInEagerMode = this._isInEagerMode();
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;				

		storeActions.setProperties(this.storeId, properties, isInEagerMode);
		return isInEagerMode ? this._buildEventPromise(storeEvents.SET_PROPERTIES) : this;
	}

	/**
	 *
	 * @param {*} callback
	 */
	apply(callback) {
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;		

		return new Promise((resolve, reject) => {
			storeActions.setState(this.storeId, resolve);
		});
	}

	/**
	 * Alias publish
	 *
	 * @alias publish
	 * @param {*} event
	 * @param {*} args
	 */
	trigger(event, ...args) {
		this.publish(event, ...args);
	}

	/**
	 *
	 * @alias publish
	 * @param {*} event
	 * @param {*} args
	 */
	dispatchEvent(event, ...args) {
		this.publish(event, ...args);
	}

	/**
	 *
	 * @param {*} event
	 * @param {*} args
	 */
	publish(event, ...args) {
		const storeEventHandler = this.storeClass.eventHandler;
		return storeEventHandler.publish(event, this.storeId, ...args);
	}

	/**
	 * Alias subscribe
	 * @param {*} event
	 * @param {*} eventHandler
	 */
	addEvent(event, eventHandler) {
		return this.subscribe(event, eventHandler);
	}

	/**
	 * @param {*} event
	 * @param {*} eventHandler
	 */
	subscribe(event, eventHandler) {
		const storeEventHandler = this.storeClass.eventHandler;
		return storeEventHandler.subscribe(event, this._eventHandlerWrapper.bind(this, eventHandler));
	}

	/**
	 *
	 * @param {*} eventHandler
	 */
	subscribeToAll(eventHandler) {
		const storeEventHandler = this.storeClass.eventHandler;

		return storeEventHandler.subscribeToAll((event, ...args) => {
			this._eventHandlerWrapper.call(this, eventHandler.bind(this, event), event, ...args)
		});
	}

	/**
	 * @private
	 */
	_getStoreItemId() {
		const store = this.storeClass.store;

		return Object.keys(store).find((storeId) => {
			const storeItem = store[storeId];
			return storeItem.id === this.id;
		});
	}

	/**
	 *
	 * @private
	 * @param {*} propertyName
	 * @param {*} propertyValue
	 */
	_propertyAccessor(propertyName, propertyValue) {
		if(typeof propertyValue === "undefined")
			return this.get(propertyName);

		return this.set({ [propertyName]: propertyValue });
	}

	/**
	 *
	 * @private
	 * @param {*} eventHandler
	 * @param {*} eventName
	 * @param {*} elementStoreId
	 * @param {*} eventArgs
	 */
	_eventHandlerWrapper(eventHandler, eventName, elementStoreId, ...eventArgs) {
		if(typeof eventHandler === 'function'
			&& elementStoreId === this.storeId) {

			eventHandler(elementStoreId, ...eventArgs);
		}
	}

	/**
	 * @private
	 * @param {*} resolveEvent
	 * @param {*} rejectEvent
	 */
	_buildEventPromise(resolveEvent, rejectEvent) {
		return new Promise((resolve, reject) => {
			const resolveSubscription = this.subscribe(resolveEvent, (...args) => {
				resolve(...args);
				resolveSubscription.unsubscribe();
			});

			if(rejectEvent) {
				const rejectSubscription = this.subscribe(rejectEvent, (...args) => {
					reject(...args);
					rejectSubscription.unsubscribe();
				});
			}
		});
	}

	/**
	 * @private
	 */
	_isInEagerMode() {
		return this.applyMode === ApplyModes.EAGER;
	}
};

export default BaseAPI;
